import { PartialRecord } from '@common/types/utils';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import { NO_FLAG, YES_FLAG } from '@common/constants/sharedConstants';

const BatchDetailsOperation = {
  Add: 'add',
  Update: 'update',
  Remove: 'remove',
} as const;

enum FormatTypes {
  NUMBER = 'NUMBER',
  DECIMAL = 'DECIMAL',
  PERCENTAGE = 'PERCENTAGE',
  PERCENT_4 = 'PERCENT_4',
  PERCENT_2 = 'PERCENT_2',
}


const FAKE_DETAIL_NAME = 'SCENARIO_GENERATED_DETAIL';
const GENERIC_DETAIL_NAME_SUFFIX = '_GENERIC_DETAIL';
const OVERRIDE_COLUMN_SUFFIX = '_OVERRIDE';
const IMPORTED_DETAIL_ID_PREFIX = 'IMPORTED-';
enum IMPORT_TYPES {
  DETAIL = 'DETAIL', // default
  OVERRIDE = 'OVERRIDE',
  DETAIL_OVERRIDE = 'DETAIL_OVERRIDE',
  DETAIL_SYNC = 'DETAIL_SYNC',
  DETAIL_SYNC_TRANSFORM = 'DETAIL_SYNC_TRANSFORM'
}
const IMPORT_TYPES_SETTINGS = {
  [IMPORT_TYPES.DETAIL]: { requireCountryTaxType: true },
  [IMPORT_TYPES.OVERRIDE]: { requireCountryTaxType: true },
  [IMPORT_TYPES.DETAIL_OVERRIDE]: { requireCountryTaxType: true },
  [IMPORT_TYPES.DETAIL_SYNC]: { requireCountryTaxType: false },
  [IMPORT_TYPES.DETAIL_SYNC_TRANSFORM]: { requireCountryTaxType: true },
};

enum DETAIL_VALUE_TRANSFORM_NAME {
  NEGATION = 'NEGATION',
  NOOP = 'NOOP',
}

const DETAIL_VALUE_TRANSFORM = {
  NEGATION: (value: number) => (-1 * value),
  NOOP: (value: number) => value,
};

const DEFAULT_ACCOUNT = 'STMAUNALOC';


const COLUMNS_TO_COPY = {
  members: [
    'MEMBER_NAME',
    'INCLUDED_FLAG',
    'COPIED_FLAG',
  ],
  details: [
    'COUNTRY_CODE',
    'COUNTRY_TAX_TYPE',
    'DETAIL_NAME',
    'NAME',
    'VALUE',
    'COMPANY_CODE',
    'LABEL',
    'FUNCTIONAL_CURRENCY',
    'TAX_CURRENCY',
    'OVERRIDE_TAX_CURRENCY',
    'ACCOUNT_EXCEPTION_ID',
    'CREATED_BY',
    'CREATED_ON',
    'UPDATED_BY',
    'UPDATED_ON',
    'ACCOUNT',
  ],
  detailsOverrides: [
    'COUNTRY_CODE',
    'COUNTRY_TAX_TYPE',
    'DETAIL_NAME',
    'NAME',
    'VALUE',
    'COMPANY_CODE',
    'LABEL',
    'FUNCTIONAL_CURRENCY',
    'TAX_CURRENCY',
    'OVERRIDE_TAX_CURRENCY',
    'ACCOUNT_EXCEPTION_ID',
    'CREATED_BY',
    'CREATED_ON',
    'UPDATED_BY',
    'UPDATED_ON',
    'ADDED',
    'DELETED',
    'MODIFIED',
    'IMPORTED',
    'ACCOUNT',
  ],
  sums: [
    'REPORT_NAME',
    'COUNTRY_CODE',
    'COUNTRY_TAX_TYPE',
    'COMPANY_CODE',
    'FUNCTIONAL_CURRENCY',
    'TAX_CURRENCY',
    'OVERRIDE_TAX_CURRENCY',
  ],
  sumsOverrides: [
    'REPORT_NAME',
    'COUNTRY_CODE',
    'COUNTRY_TAX_TYPE',
    'COMPANY_CODE',
    'NAME',
    'VALUE',
  ],
  companyCodesOverrides: [
    'PERIOD_NAME',
    'ENABLED_FLAG',
    'COMPANY_CODE',
    'RECORD_FTC',
    'CC_DESCRIPTION',
    'BUSINESS_UNIT',
    'LEGAL_ENTITY',
    'FUNCTIONAL_CURRENCY',
    'TAX_CURRENCY',
    'OVERRIDE_TAX_CURRENCY',
    'ASSOCIATED_LE_COUNTRY_CODE',
    'OVERRIDE_SECONDARY_TAX',
    'STAT_CALC',
    'ADDED',
    'MODIFIED',
    'DELETED',
  ],
  obligationsOverrides: [
    'PERIOD_NAME',
    'COMPANY_CODE',
    'COUNTRY_TAX_TYPE',
    'COUNTRY_CODE',
    'OVERRIDE_TAX_RATE',
    'OVERRIDE_DEF_TAX_RATE',
    'RECORD_VA',
    'NOL',
    'FED_FTC_LIMIT',
    'STATE_MINIMUM_TAX',
    'STATE_PRE_POST_NOL',
    'STATE_APPT_RATE',
    'OVERRIDE_TAX_CURRENCY',
    'ADDED',
    'MODIFIED',
    'DELETED',
  ],
  scenarioAssignmentMap: [
    'PERIOD_NAME',
    'ASSIGNMENT',
    'COMPANY_CODE',
    'ASSIGNMENT_TYPE',
    'CURRENT_PERIOD_ENTITY',
    'CC_PROVISION_FLAG',
    'CC_ENABLED_FLAG',
  ],
};

const SCENARIO_DATA_TABLES = {
  scenarios: 'TLS_SCENARIO',
  members: 'TLS_SCENARIO_MEMBERS',
  companyCodes: 'TPC_COMPANY_CODE',
  companyCodesOverrides: 'TLS_SCENARIO_COMPANY_CODES',
  countryTaxTypes: 'TPC_COUNTRY_TAX_TYPES',
  taxExceptions: 'TLS_BOOK_TO_TAX_DIFF_CALC',
  accounts: 'TPC_ACCOUNTS',
  obligations: 'TLS_OBLIGATION',
  obligationsOverrides: 'TLS_SCENARIO_OBLIGATIONS',
  adjustments: 'TPC_ADJUSTMENT',
  balances: 'TLS_SCENARIO_BALANCES',
  taxEntries: 'TPC_TAX_ENTRY',
  conversions: 'TLS_REPORTING_CONV',
  country: 'TPC_COUNTRY',
  userAssignments: 'TPC_USER_ASSIGNMENTS_V',
  periodAssignmentMap: 'TLS_PERIOD_ASSIGNMENT_MAP',
  userAssignment: 'TPC_USER_ASSIGNMENT',
  period: 'TPC_PERIOD',
  details: 'TLS_SCENARIO_DETAILS',
  detailsOverrides: 'TLS_SCENARIO_DETAILS_OVR',
  sums: 'TLS_SCENARIO_SUMS',
  sumsOverrides: 'TLS_SCENARIO_SUMS_OVR',
  status: 'TPC_STATUS',
  provisionStatus: 'TPC_PROVISION_STATUS',
  scenarioAssignmentMap: 'TLS_SCENARIO_ASSIGNMENT_MAP',
  tlsNonUsDtl: 'TLS_NONUS_DTL',
};

const COLUMNS = {
  TPC_PERIOD: {
    PERIOD_NAME: 'PERIOD_NAME',
    PERIOD_YEAR: 'PERIOD_YEAR',
    PERIOD_NUM: 'PERIOD_NUM',
  },
  MEMBERS: {
    SCENARIO_ID: 'SCENARIO_ID',
    MEMBER_NAME: 'MEMBER_NAME',
    INCLUDED_FLAG: 'INCLUDED_FLAG',
    COPIED_FLAG: 'COPIED_FLAG',
  },
  SCENARIOS: {
    SCENARIO_ID: 'SCENARIO_ID',
    SCENARIO_NAME: 'SCENARIO_NAME',
    PERIOD_NAME: 'PERIOD_NAME',
    PUBLIC_FLAG: 'PUBLIC_FLAG',
    DATA_SOURCE: 'DATA_SOURCE',
    SOURCE_NAME: 'SOURCE_NAME',
    ENABLED_FLAG: 'ENABLED_FLAG',
  },
  TRIAL_BALANCE: {
    SCENARIO_ID: 'SCENARIO_ID',
    AMOUNT: 'YTD_BALANCE',
    ACCOUNT: 'R2R_ACCOUNT',
    COMPANY_CODE: 'COMPANY_CODE',
  },
  ADJUSTMENTS: {
    ID: 'ADJUSTMENT_ID',
    SCENARIO_ID: 'SCENARIO_ID',
    ACCOUNT: 'ACCOUNT',
    AMOUNT: 'ADJUSTMENT_AMOUNT',
    MULTIPLIER: 'MULTIPLIER',
    DESCRIPTION: 'ADJUSTMENT_DESCRIPTION',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COMPANY_CODE: 'COMPANY_CODE',
  },
  BOOK_TO_TAX: {
    ID: 'EXCEPTION_ID',
    SCENARIO_ID: 'SCENARIO_ID',
    ACCOUNT: 'EXCEPTION_ACCOUNT',
    DESCRIPTION: 'DESCRIPTION',
    EXCEPTION_BASIS: 'EXCEPTION_BASIS',
    EXCEPTION_ACCOUNT: 'EXCEPTION_ACCOUNT',
    MULTIPLIER: 'DISALLOWED_PERCENTAGE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COMPANY_CODE: 'COMPANY_CODE',
  },
  OBLIGATIONS: {
    PERIOD_NAME: 'PERIOD_NAME',
    SCENARIO_ID: 'SCENARIO_ID',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    OVERRIDE_TAX_RATE: 'OVERRIDE_TAX_RATE',
    OVERRIDE_DEF_TAX_RATE: 'OVERRIDE_DEF_TAX_RATE',
    RECORD_VA: 'RECORD_VA',
    NOL: 'NOL',
    FED_FTC_LIMIT: 'FED_FTC_LIMIT',
    STATE_MINIMUM_TAX: 'STATE_MINIMUM_TAX',
    STATE_PRE_POST_NOL: 'STATE_PRE_POST_NOL',
    STATE_APPT_RATE: 'STATE_APPT_RATE',
    OVERRIDE_TAX_CURRENCY: 'OVERRIDE_TAX_CURRENCY',
    ENABLED_FLAG: 'ENABLED_FLAG',
  },
  COMPANY_CODES: {
    PERIOD_NAME: 'PERIOD_NAME',
    SCENARIO_ID: 'SCENARIO_ID',
    COMPANY_CODE: 'COMPANY_CODE',
    RECORD_FTC: 'RECORD_FTC',
    CC_DESCRIPTION: 'CC_DESCRIPTION',
    BUSINESS_UNIT: 'BUSINESS_UNIT',
    BUSINESS_CODE: 'BUSINESS_CODE',
    LEGAL_ENTITY: 'LEGAL_ENTITY',
    CONN_NAME: 'CONN_NAME',
    FUNCTIONAL_CURRENCY: 'FUNCTIONAL_CURRENCY',
    TAX_CURRENCY: 'TAX_CURRENCY',
    OVERRIDE_TAX_CURRENCY: 'OVERRIDE_TAX_CURRENCY',
    ENABLED_FLAG: 'ENABLED_FLAG',
    ASSOCIATED_LE_COUNTRY_CODE: 'ASSOCIATED_LE_COUNTRY_CODE',
    STAT_CURRENCY: 'STAT_CURRENCY',
    OVERRIDE_SECONDARY_TAX: 'OVERRIDE_SECONDARY_TAX',
    SL_FLAG: 'SL_FLAG',
    STAT_CALC: 'STAT_CALC',
  },
  COUNTRY_TAX_TYPES: {
    SCENARIO_ID: 'SCENARIO_ID',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    TAX_RATE: 'TAX_RATE',
    DEF_TAX_RATE: 'DEF_TAX_RATE',
    ENABLED_FLAG: 'ENABLED_FLAG',
    DESCRIPTION: 'DESCRIPTION',
  },
  TAX_ENTRIES: {
    SCENARIO_ID: 'SCENARIO_ID',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COMPANY_CODE: 'COMPANY_CODE',
    TAX_ENTRY_TYPE: 'TAX_ENTRY_TYPE',
    TAX_ENTRY_ACTIVITY: 'TAX_ENTRY_ACTIVITY',
    ACCOUNT: 'ACCOUNT',
    REVIEWED_FLAG: 'REVIEWED_FLAG',
    GROSS_AMOUNT: 'GROSS_AMOUNT',
    CURRENCY_CODE: 'CURRENCY_CODE',
  },
  DETAILS: {
    DETAIL_ID: 'DETAIL_ID',
    ACCOUNT_EXCEPTION_ID: 'ACCOUNT_EXCEPTION_ID',
    SCENARIO_ID: 'SCENARIO_ID',
    NAME: 'NAME',
    LABEL: 'LABEL',
    DETAIL_NAME: 'DETAIL_NAME',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    VALUE: 'VALUE',
    FUNCTIONAL_CURRENCY: 'FUNCTIONAL_CURRENCY',
    OVERRIDE_TAX_CURRENCY: 'OVERRIDE_TAX_CURRENCY',
    ADDED: 'ADDED',
    MODIFIED: 'MODIFIED',
    DELETED: 'DELETED',
    IMPORTED: 'IMPORTED',
    ACCOUNT: 'ACCOUNT',
  },
  DETAILS_OVERRIDES: {
    DETAIL_ID: 'DETAIL_ID',
    ACCOUNT_EXCEPTION_ID: 'ACCOUNT_EXCEPTION_ID',
    SCENARIO_ID: 'SCENARIO_ID',
    NAME: 'NAME',
    LABEL: 'LABEL',
    DETAIL_NAME: 'DETAIL_NAME',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    VALUE: 'VALUE',
    FUNCTIONAL_CURRENCY: 'FUNCTIONAL_CURRENCY',
    OVERRIDE_TAX_CURRENCY: 'OVERRIDE_TAX_CURRENCY',
    ADDED: 'ADDED',
    MODIFIED: 'MODIFIED',
    DELETED: 'DELETED',
    IMPORTED: 'IMPORTED',
    ACCOUNT: 'ACCOUNT',
  },

  SUMS: {
    SCENARIO_ID: 'SCENARIO_ID',
    REPORT_NAME: 'REPORT_NAME',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    FUNCTIONAL_CURRENCY: 'FUNCTIONAL_CURRENCY',
    CURRENCY_RATE: 'CURRENCY_RATE',
    OVERRIDE_TAX_CURRENCY: 'OVERRIDE_TAX_CURRENCY',
  },
  SUMS_OVERRIDES: {
    SCENARIO_ID: 'SCENARIO_ID',
    REPORT_NAME: 'REPORT_NAME',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    NAME: 'NAME',
    VALUE: 'VALUE',
    IMPORTED_FLAG: 'IMPORTED_FLAG',
  },
  CONVERSIONS: {
    SCENARIO_ID: 'SCENARIO_ID',
    PERIOD_NAME: 'PERIOD_NAME',
    FROM_CURRENCY: 'FROM_CURRENCY',
    TO_CURRENCY: 'TO_CURRENCY',
    OPEN_SPOT_RATE: 'OPEN_SPOT_RATE',
    END_SPOT_RATE: 'END_SPOT_RATE',
    MON_OPER_RATE: 'MON_OPER_RATE',
    WTD_AVG_RATE: 'WTD_AVG_RATE',
    FROM_CURR_PREC: 'FROM_CURR_PREC',
    TO_CURR_PREC: 'TO_CURR_PREC',
  },
  ACCOUNTS: {
    ACCOUNT: 'ACCOUNT',
    DESCRIPTION: 'DESCRIPTION',
    LINE_OF_BUSINESS: 'LINE_OF_BUSINESS',
    ENABLED_FLAG: 'ENABLED_FLAG',
    SUMMARY_FLAG: 'SUMMARY_FLAG',
    PERIOD_NAME: 'PERIOD_NAME',
    SAP_ACCOUNT: 'SAP_ACCOUNT',
    MARS_ACCOUNT: 'MARS_ACCOUNT',
    MARS_DESCRIPTION: 'MARS_DESCRIPTION',
    USED_BY: 'USED_BY',
    PARSED: 'PARSED',
    SAP_PARSED: 'SAP_PARSED',
    CREATED_SSO: 'CREATED_SSO',
    UPDATED_SSO: 'UPDATED_SSO',
    ALLOW_ADJ_FLAG: 'ALLOW_ADJ_FLAG',
    PRE_TAX_FLAG: 'PRE_TAX_FLAG',
  },
  PROVISION_STATUS: {
    COMPANY_CODE: 'COMPANY_CODE',
    PERIOD_NAME: 'PERIOD_NAME',
    STATUS_ID: 'STATUS_ID',
  },
  PERIOD_ASSIGNMENT_MAP: {
    PERIOD_NAME: 'PERIOD_NAME',
    COMPANY_CODE: 'COMPANY_CODE',
    ASSIGNMENT_TYPE: 'ASSIGNMENT_TYPE',
    ASSIGNMENT: 'ASSIGNMENT',
    CC_ENABLED_FLAG: 'CC_ENABLED_FLAG',
  },
  SCENARIO_ASSIGNMENT_MAP: {
    SCENARIO_ID: 'SCENARIO_ID',
    COMPANY_CODE: 'COMPANY_CODE',
    ASSIGNMENT_TYPE: 'ASSIGNMENT_TYPE',
    ASSIGNMENT: 'ASSIGNMENT',
  },
  USER_ASSIGNMENT: {
    ASSIGNMENT_TYPE: 'ASSIGN_TYPE',
    ASSIGNMENT: 'ASSIGNMENT',
    SSO: 'SSO',
  },
  STATUS: {
    STATUS_ID: 'STATUS_ID',
    STATUS_NAME: 'STATUS_NAME',
  },
  METADATA: {
    ADDED: 'ADDED',
    MODIFIED: 'MODIFIED',
    DELETED: 'DELETED',
  },
  TLS_NONUS_DTL: {
    DESCR: 'DESCR',
    COMPANY_CODE: 'COMPANY_CODE',
    COUNTRY_CODE: 'COUNTRY_CODE',
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    FUNC_CURRENCY: 'FUNC_CURRENCY',
    DISALLOWED_AMT: 'DISALLOWED_AMT',
    TE_ADJ_ID: 'TE_ADJ_ID',
    ACCOUNT: 'ACCOUNT',
    PERIOD_NAME: 'PERIOD_NAME',
    JOB_ID: 'JOB_ID',
  },
  TPC_PROVISION_STATUS: {
    CALC_JOB_ID: 'CALC_JOB_ID',
    COMPANY_CODE: 'COMPANY_CODE',
    PERIOD_NAME: 'PERIOD_NAME',
  },
  TLS_COMMON_SUMS_COLUMNS: {
    COUNTRY_TAX_TYPE: 'COUNTRY_TAX_TYPE',
    COMPANY_CODE: 'COMPANY_CODE',
    FUNC_CURRENCY: 'FUNC_CURRENCY',
    OVERRIDE_TAX_CUR: 'OVERRIDE_TAX_CUR',
  },
};

const OUTPUT_COLUMN_NAMES = {
  countryCode: 'COUNTRY_CODE',
  countryTaxType: 'COUNTRY_TAX_TYPE',
  detailName: 'DETAIL_NAME',
  name: 'NAME',
  label: 'LABEL',
  value: 'VALUE',
  aggregation: 'AGGREGATION',
  companyCode: 'COMPANY_CODE',
  scenarioId: 'SCENARIO_ID',
};

export type DetailDefinition = {
  name: string,
  detailName?: string,
  label?: string,
  tb?: { BOOK_TYPE: string | string[] },
  adj?: { ACCOUNT_ADJ_FLAG?: string, TYPE_NAME: string, BOOK_TYPE?: string },
  btt?: { EXCEPTION_BASIS: string },
  preTax?: boolean,
  negative?: boolean,
  originalCalc?: { STEP: string, EXCEPTION_BASIS?: string, US?: boolean },
  sum?: {
    tb?: { BOOK_TYPE: string },
    adj?: { ACCOUNT_ADJ_FLAG: string, TYPE_NAME: string },
    btt?: { EXCEPTION_BASIS: string },
    preTax?: boolean,
    negative?: boolean,
    sum?: true,
  }[],
};

const DETAILS_DEFINITIONS: DetailDefinition[] = [
  {
    // GAAP Income/(Loss) Before Tax
    name: 'IBT_DETAILS',
    tb: { BOOK_TYPE: 'P' },
    preTax: true,
    negative: true,
  },
  {
    // Pre-Tax Adjustment
    name: 'IBT_ADJ_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'B' },
  },
  {
    // GAAP to STAT Non-Reversing Differences
    name: 'G2S_BTT_PERM_DETAILS',
    tb: { BOOK_TYPE: 'G' },
    btt: { EXCEPTION_BASIS: 'G' },
    originalCalc: { STEP: '2A', US: false },
  },
  {
    // GAAP to STAT Non-Reversing Differences
    name: 'G2S_MANUAL_PERM_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'G', BOOK_TYPE: 'G' },
    originalCalc: { STEP: '2C', US: false },
  },
  {
    // GAAP to STAT Non-Reversing Differences
    name: 'G2S_AB_PERM_DETAILS',
    tb: { BOOK_TYPE: 'G' },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'G', BOOK_TYPE: 'G' },
    originalCalc: { STEP: '2B', US: false },
  },
  {
    // GAAP to STAT Reversing Differences
    name: 'G2S_MANUAL_TEMP_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'O' },
    originalCalc: { STEP: '3C', US: false },
  },
  {
    // GAAP to STAT Reversing Differences
    name: 'STMG2STEMP',
    detailName: 'STMG2STEMP GAAP to STAT Reversing Differences',
    sum: [
      {
        // STMG2STEMP_TEMP
        tb: { BOOK_TYPE: 'G' },
        preTax: true,
        negative: true,
        sum: true,
      },
      {
        // G2S BTT Perm
        tb: { BOOK_TYPE: 'G' },
        btt: { EXCEPTION_BASIS: 'G' },
        negative: true,
        sum: true,
      },
    ],
    originalCalc: { STEP: '3A', US: false },
  },
  {
    // Tax Permanent Differences
    name: 'GAAP_BTT_PERM_DETAILS',
    tb: { BOOK_TYPE: ['P', 'G'] },
    btt: { EXCEPTION_BASIS: 'P' },
    originalCalc: { STEP: '5A', US: false },
  },
  {
    // Tax Permanent Differences
    name: 'GAAP_MANUAL_PERM_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'P' },
    originalCalc: { STEP: '5C', EXCEPTION_BASIS: 'P' },
  },
  {
    // Tax Permanent Differences
    name: 'GAAP_A_PERM_DETAILS',
    tb: { BOOK_TYPE: ['P', 'G'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'P' },
    originalCalc: { STEP: '5B', US: false },
  },
  {
    // Tax Temporary Differences
    name: 'GAAP_BTT_TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P', 'G'] },
    btt: { EXCEPTION_BASIS: 'T' },
    originalCalc: { STEP: '6A', US: false },
  },
  {
    // Tax Temporary Differences
    name: 'GAAP_MANUAL_TEMP_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'T' },
    originalCalc: { STEP: '6C', EXCEPTION_BASIS: 'T', US: false },
  },
  {
    // Tax Temporary Differences
    name: 'GAAP_AB_TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P', 'G'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'T' },
    originalCalc: { STEP: '6B', US: false },
  },
  {
    // Total Credits
    name: 'GAAP_AB_CREDIT_DETAILS',
    tb: { BOOK_TYPE: ['P', 'G'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'TAXCREDIT' },
    originalCalc: { STEP: '8A1' },
  },
  {
    // Total Credits
    name: 'GAAP_MANUAL_CREDIT_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'TAXCREDIT' },
    originalCalc: { STEP: '8A2' },
  },
  {
    name: 'PR_CR_UTL_DETAILS',
    adj: {
      TYPE_NAME: 'DTAXCREDIT',
    },
  },
  {
    name: 'DEF_PR_CR_UTL_DETAILS',
    adj: {
      TYPE_NAME: 'DTAXCREDIT',
    },
    negative: true,
  },
  // Federal Perm
  {
    name: 'FEDERAL_BTT_PERM_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    btt: { EXCEPTION_BASIS: 'P' },
    originalCalc: { STEP: '5A', EXCEPTION_BASIS: 'P', US: true },
  },
  {
    // Tax Permanent Differences
    name: 'FEDERAL_A_PERM_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'P' },
    originalCalc: { STEP: '5B', EXCEPTION_BASIS: 'P', US: true },
  },

  // Federal Temp
  {
    name: 'FEDERAL_BTT_TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    btt: { EXCEPTION_BASIS: 'T' },
    originalCalc: { STEP: '6A', EXCEPTION_BASIS: 'T', US: true },
  },
  {
    name: 'FEDERAL_TEMP_MANUAL_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'T' },
    originalCalc: { STEP: '6C', EXCEPTION_BASIS: 'T', US: true },
  },
  {
    // Tax Temporary Differences
    name: 'FEDERAL_AB_TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'T' },
    originalCalc: { STEP: '6B', EXCEPTION_BASIS: 'T', US: true },
  },

  {
    // State Perm Mods
    name: 'PERM_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'M' },
    originalCalc: { STEP: '5B', EXCEPTION_BASIS: 'M', US: true },
  },
  {
    // State Perm Mods
    name: 'PERM_MANUAL_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'M' },
    originalCalc: { STEP: '5C', EXCEPTION_BASIS: 'M', US: true },
  },
  {
    // State Perm Mods
    name: 'BTT_PERM_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    btt: { EXCEPTION_BASIS: 'M' },
    originalCalc: { STEP: '5A', EXCEPTION_BASIS: 'M', US: true },
  },

  {
    // State Temp Mods
    name: 'TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    adj: { ACCOUNT_ADJ_FLAG: 'Y', TYPE_NAME: 'N' },
    originalCalc: { STEP: '6B', EXCEPTION_BASIS: 'N', US: true },
  },
  {
    // State Temp Mods
    name: 'TEMP_MANUAL_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'N' },
    originalCalc: { STEP: '6C', EXCEPTION_BASIS: 'N', US: true },
  },
  {
    // State Temp Mods
    name: 'BTT_TEMP_DETAILS',
    tb: { BOOK_TYPE: ['P'] },
    btt: { EXCEPTION_BASIS: 'N' },
    originalCalc: { STEP: '6A', EXCEPTION_BASIS: 'N', US: true },
  },

  {
    name: 'STATE_CUR_TAX_ADJ_DETAILS',
    adj: { ACCOUNT_ADJ_FLAG: 'N', TYPE_NAME: 'S' },
    originalCalc: { STEP: '6C', EXCEPTION_BASIS: 'S', US: true },
  },
];

const SOURCE_TABLES = {
  NON_US: 'TLS_NONUS',
  US_FEDERAL: 'TLS_US',
  US_STATE: 'TLS_US',
};

enum IMPORT_VALIDATION_RULES {
  PRE_TAX_ACCOUNT = 'PRE_TAX_ACCOUNT',
  OBLIGATION_NOL_UTILIZE = 'OBLIGATION_NOL_UTILIZE',
  OBLIGATION_NOL_RECORD = 'OBLIGATION_NOL_RECORD',
  OBLIGATION_STATE_PRE_NOL = 'OBLIGATION_STATE_PRE_NOL',
  OBLIGATION_STATE_POST_NOL = 'OBLIGATION_STATE_POST_NOL',
}

const EDITABLE_FIELD_CONDITIONS = { // eslint-disable-line @typescript-eslint/no-unused-vars
  OBLIGATION_NOL_UTILIZE: 'OBLIGATION_NOL_UTILIZE',
  OBLIGATION_NOL_RECORD: 'OBLIGATION_NOL_RECORD',
  OBLIGATION_STATE_PRE_NOL: 'OBLIGATION_STATE_PRE_NOL',
  OBLIGATION_STATE_POST_NOL: 'OBLIGATION_STATE_POST_NOL',
};

enum VISIBLE_FIELD_CONDITIONS {
  COMPANY_CODE_STAT_CALC_DISABLED = 'COMPANY_CODE_STAT_CALC_DISABLED',
}

export type ReportName = 'NON_US' | 'US_FEDERAL' | 'US_STATE';

export type Sum = {
  name: string,
  formula: string | [...({ when: string[], then: string }[]), string | { when: string[], then: string }],
  disableCurrencyConversion?: boolean,
  enableRounding?: boolean,
  roundingPrecision?: number,
  import?: {
    tag: string,
    type: IMPORT_TYPES.DETAIL_SYNC,
    reports: PartialRecord<ReportName, string>,
    validationRules?: null | false,
  } | {
    tag: string,
    type?: Exclude<IMPORT_TYPES, IMPORT_TYPES.DETAIL_SYNC>,
    validationRules?: IMPORT_VALIDATION_RULES[],
    syncTransform?: { [detailName: string]: DETAIL_VALUE_TRANSFORM_NAME }
  }
};
export type Report = {
  NAME: ReportName,
  CONDITION: string[],
  SUMS: Sum[],
};
const REPORTS: Report[] = [
  {
    NAME: 'NON_US',
    CONDITION: ['NON_US'],
    SUMS: [
      {
        name: 'GAAP_IBT',
        formula: `{{sum 'IBT_DETAILS'}}`,
        import: {
          tag: 'IBT',
          type: IMPORT_TYPES.DETAIL_SYNC,
          reports: {
            NON_US: `GAAP_IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            US_FEDERAL: `IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
          },
        },
      },
      {
        name: 'GAAP_IBT_ADJ',
        formula: `{{sum 'IBT_ADJ_DETAILS'}}`,
        import: {
          tag: 'PRETAXADJ',
          validationRules: [
            IMPORT_VALIDATION_RULES.PRE_TAX_ACCOUNT,
          ],
        },
      },
      {
        name: 'ADJ_GAAP_IBT',
        formula: `{{GAAP_IBT}} + {{GAAP_IBT_ADJ}}`,
      },
      {
        name: 'G2S_TEMP',
        formula: `{{sum 'STMG2STEMP' 'G2S_MANUAL_TEMP_DETAILS'}}`,
        import: {
          tag: 'G2SREV',
        },
      },
      {
        name: 'G2S_PERM',
        formula: `{{sum 'G2S_BTT_PERM_DETAILS' 'G2S_MANUAL_PERM_DETAILS' 'G2S_AB_PERM_DETAILS'}}`,
        import: {
          tag: 'G2SNONREV',
        },
      },
      {
        name: 'STAT_IBT',
        formula: `{{G2S_TEMP}} + {{G2S_PERM}} + {{ADJ_GAAP_IBT}}`,
      },
      {
        name: 'B2T_PERM',
        formula: `{{sum 'GAAP_BTT_PERM_DETAILS' 'GAAP_MANUAL_PERM_DETAILS' 'GAAP_A_PERM_DETAILS'}}`,
        import: {
          tag: 'B2TPERM',
        },
      },
      {
        name: 'B2T_TEMP',
        formula: `{{sum 'GAAP_BTT_TEMP_DETAILS' 'GAAP_MANUAL_TEMP_DETAILS' 'GAAP_AB_TEMP_DETAILS'}}`,
        import: {
          tag: 'B2TTEMP',
        },
      },

      // GAAP Current Tax Expense/(Benefit) Before Credits
      {
        name: 'TXBL_INC',
        formula: `{{STAT_IBT}} + {{B2T_PERM}} + {{B2T_TEMP}}`,
      },
      {
        name: 'UTILIZE_PY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}}='UTILIZE'`,
            ],
            then: `{{NOL_TAX_ENTRIES}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'UTILNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'TXBL_INC_AFT_NOL',
        formula: `{{TXBL_INC}} + {{UTILIZE_PY_NOL}}`,
      },
      {
        name: 'RECORD_CY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{TXBL_INC_AFT_NOL}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'RECNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'TAX_RATE',
        formula: `{{SOURCE_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'CURTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'GAAP_CUR_TAX_B4_CR',
        formula: `({{TXBL_INC_AFT_NOL}} + {{RECORD_CY_NOL}}) * {{TAX_RATE}}`,
        enableRounding: true,
      },

      // tax credit
      {
        name: 'GAAP_TAX_CR_ADJ', // seems to be broken in old calc, to be discussed???
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS'}}`,
      },
      {
        name: 'TOTAL_CR',
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS' 'GAAP_AB_CREDIT_DETAILS' 'PR_CR_UTL_DETAILS'}}`,
        import: {
          tag: 'TOTCR',
        },
      },
      {
        name: 'GAAP_DEF_PR_CR_UTL',
        formula: `{{sum 'DEF_PR_CR_UTL_DETAILS' }}`,
        import: {
          tag: 'AFTAXDEFTE',
          type: IMPORT_TYPES.DETAIL_SYNC_TRANSFORM,
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
      },

      // Deferred taxes
      {
        name: 'B2T_TEMP_ADJ',
        formula: `-{{B2T_TEMP}}`,
      },
      {
        name: 'G2S_DEF_TAX_P',
        formula: `-{{G2S_TEMP}}`,
      },
      {
        name: 'G2S_DEF_TAX_G',
        formula: `{{G2S_TEMP}}`,
      },
      {
        name: 'NO_NOL',
        formula: `-{{UTILIZE_PY_NOL}} - {{RECORD_CY_NOL}}`,
      },
      {
        name: 'RECORD_VA',
        formula: [
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='NOL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{NO_NOL}}`,
          },
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='ALL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-({{B2T_TEMP_ADJ}} + {{G2S_DEF_TAX_P}} + {{G2S_DEF_TAX_G}} + {{NO_NOL}})`,
          },
          `NULL`,
        ],
      },
      {
        name: 'TOT_DEF',
        formula: `{{B2T_TEMP_ADJ}} + {{G2S_DEF_TAX_P}} + {{G2S_DEF_TAX_G}} + {{NO_NOL}} + {{RECORD_VA}}`,
        // import: {
        //   tag: 'TOTSTATDEF',
        // },
      },
      {
        name: 'DEF_TAX_RATE',
        formula: `{{SOURCE_DEF_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'DEFTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'GAAP_DEF_TAX',
        formula: `{{TOT_DEF}} * {{DEF_TAX_RATE}}`,
        enableRounding: true,
      },

      // Tax Expense on CY Activity - STAT Functional Currency
      {
        name: 'GAAP_CUR_TAX',
        formula: `{{TOTAL_CR}} + {{GAAP_CUR_TAX_B4_CR}}`,
      },
      {
        name: 'TAX_EXP_CY',
        formula: `{{GAAP_CUR_TAX}} + {{GAAP_DEF_TAX}}`,
      },
      {
        name: 'CY_EFF_TAX_RATE',
        formula: `{{TAX_EXP_CY}} / {{orNull 'STAT_IBT'}}`,
        disableCurrencyConversion: true,
        enableRounding: true,
        roundingPrecision: 6,
      },

      // GAAP
      {
        name: 'GAAP_G2S_DEF_TAX_G',
        formula: '0',
      },
      {
        name: 'GAAP_RECORD_VA',
        formula: [
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='NOL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{NO_NOL}}`,
          },
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='ALL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-({{B2T_TEMP_ADJ}} + {{G2S_DEF_TAX_P}} + {{GAAP_G2S_DEF_TAX_G}} + {{NO_NOL}})`,
          },
          `NULL`,
        ],
      },
      {
        name: 'GAAP_TOT_DEF',
        formula: `{{B2T_TEMP_ADJ}} + {{G2S_DEF_TAX_P}} + {{GAAP_G2S_DEF_TAX_G}} + {{NO_NOL}} + {{GAAP_RECORD_VA}}`,
        // import: {
        //   tag: 'TOTGAAPDEF',
        // },
      },
      {
        name: 'GAAP_GAAP_DEF_TAX',
        formula: `{{GAAP_TOT_DEF}} * {{DEF_TAX_RATE}} + {{GAAP_DEF_PR_CR_UTL}}`,
        enableRounding: true,
      },
      {
        name: 'GAAP_TAX_EXP_CY',
        formula: `{{GAAP_CUR_TAX}} + {{GAAP_GAAP_DEF_TAX}}`,
      },
      {
        name: 'GAAP_CY_EFF_TAX_RATE',
        formula: `{{GAAP_TAX_EXP_CY}} / {{orNull 'GAAP_IBT'}}`,
        disableCurrencyConversion: true,
        enableRounding: true,
        roundingPrecision: 6,
      },
      {
        name: 'CUR_FTC',
        formula: [
          {
            when: [
              `{{SOURCE_RECORD_FTC}} = 'Y'`,
            ],
            then: `-{{GAAP_CUR_TAX}}`,
          },
          `null`,
        ],
        enableRounding: true,
      },
      {
        name: 'DEF_FTC',
        formula: [
          {
            when: [
              `{{SOURCE_RECORD_FTC}} = 'Y'`,
            ],
            then: `-{{GAAP_GAAP_DEF_TAX}}`,
          },
          `null`,
        ],
        enableRounding: true,
      },
    ],
  },
  {
    NAME: 'US_FEDERAL',
    CONDITION: ['US', 'NO_SUBDIVISION'],
    SUMS: [
      {
        name: 'IBT',
        formula: `{{sum 'IBT_DETAILS'}}`,
        import: {
          tag: 'IBT',
          type: IMPORT_TYPES.DETAIL_SYNC,
          reports: {
            NON_US: `GAAP_IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            US_FEDERAL: `IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
          },
        },
      },
      {
        name: 'IBT_ADJ',
        formula: `{{sum 'IBT_ADJ_DETAILS'}}`,
        import: {
          tag: 'PRETAXADJ',
          validationRules: [
            IMPORT_VALIDATION_RULES.PRE_TAX_ACCOUNT,
          ],
        },
      },
      {
        name: 'ADJ_IBT',
        formula: `{{IBT}} + {{IBT_ADJ}}`,
      },
      {
        name: 'STATE_CUR_TAX',
        formula: `-{{report 'US_STATE' 'CUR_TAX'}}`,
        enableRounding: true,
      },
      {
        name: 'STATE_CUR_TAX_ADJ',
        formula: `{{sum 'STATE_CUR_TAX_ADJ_DETAILS'}}`, // adjustments matching 'STATE ADJUSTMENT'
        import: {
          tag: 'FEDSTADJ',
        },
      },
      {
        name: 'PERM',
        formula: `{{sum 'FEDERAL_BTT_PERM_DETAILS' 'GAAP_MANUAL_PERM_DETAILS' 'FEDERAL_A_PERM_DETAILS'}}`,
        import: {
          tag: 'B2TPERM',
        },
      },
      {
        name: 'TEMP',
        formula: `{{sum 'FEDERAL_BTT_TEMP_DETAILS' 'FEDERAL_TEMP_MANUAL_DETAILS' 'FEDERAL_AB_TEMP_DETAILS'}}`,
        import: {
          tag: 'B2TTEMP',
        },
      },
      {
        // taxable income/loss
        name: 'TXBL_INC',
        formula: `{{ADJ_IBT}} + {{STATE_CUR_TAX}} + {{STATE_CUR_TAX_ADJ}} + {{PERM}} + {{TEMP}}`,
      },
      {
        name: 'UTILIZE_PY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}}='UTILIZE'`,
            ],
            then: `{{NOL_TAX_ENTRIES}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'UTILNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        // Taxable Income After NOL Utilization
        name: 'TXBL_INC_AFT_NOL',
        formula: `{{TXBL_INC}} + {{UTILIZE_PY_NOL}}`,
      },
      {
        name: 'RECORD_CY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{TXBL_INC_AFT_NOL}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'RECNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'TAX_RATE',
        formula: `{{SOURCE_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'CURTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'CUR_TAX_B4_CR',
        formula: `({{TXBL_INC_AFT_NOL}} + {{RECORD_CY_NOL}}) * {{TAX_RATE}}`,
        enableRounding: true,
      },
      {
        name: 'TAX_CR_ADJ',
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS' 'GAAP_AB_CREDIT_DETAILS'}}`,
      },
      {
        name: 'TOTAL_CR',
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS' 'GAAP_AB_CREDIT_DETAILS' 'PR_CR_UTL_DETAILS'}}`,
        import: {
          tag: 'TOTCR',
        },
      },
      {
        name: 'DEF_PR_CR_UTL',
        formula: `{{sum 'DEF_PR_CR_UTL_DETAILS' }}`,
        import: {
          tag: 'AFTAXDEFTE',
          type: IMPORT_TYPES.DETAIL_SYNC_TRANSFORM,
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
      },
      {
        name: 'CUR_FTC',
        formula: `{{report 'NON_US' 'CUR_FTC'}}`,
        // import: {
        //   tag: 'FEDCURFTC',
        // },
      },
      {
        name: 'DEF_FTC',
        formula: `{{report 'NON_US' 'DEF_FTC'}}`,
        // import: {
        //   tag: 'FEDDEFFTC',
        // },
      },
      {
        name: 'TOT_FTC',
        formula: `{{CUR_FTC}} + {{DEF_FTC}}`,
      },
      {
        name: 'FTC_LIMIT',
        formula: [
          {
            when: [
              `{{OBLIGATION_FED_FTC_LIMIT}} = 'Y'`,
            ],
            then: `-({{report 'NON_US' 'TXBL_INC_AFT_NOL'}} + {{report 'NON_US' 'TOT_DEF'}}) * {{TAX_RATE}}`,
          },
          `0`,
        ],
        // import: {
        //   tag: 'FTCLIMIT',
        // },
      },
      {
        name: 'BEN_FTC_CR',
        formula: [
          {
            when: [
              `{{OBLIGATION_FED_FTC_LIMIT}} = 'Y'`,
              `abs({{TOT_FTC}}) > abs({{FTC_LIMIT}})`,
            ],
            then: `{{FTC_LIMIT}}`,
          },
          `{{TOT_FTC}}`,
        ],
      },
      {
        name: 'CY_TEMP',
        formula: `-{{TEMP}}`,
      },
      {
        name: 'NO_NOL',
        formula: `-({{RECORD_CY_NOL}} + {{UTILIZE_PY_NOL}})`,
      },
      {
        name: 'FBOS',
        formula: `-{{report 'US_STATE' 'DEF_TAX'}}`,
        enableRounding: true,
      },
      {
        name: 'RECORD_VA',
        formula: [
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='NOL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{NO_NOL}}`,
          },
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='ALL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-({{CY_TEMP}} + {{NO_NOL}} + {{FBOS}})`,
          },
          `NULL`,
        ],
      },
      {
        name: 'TOT_DEF',
        formula: `{{CY_TEMP}} + {{NO_NOL}} + {{FBOS}} + {{RECORD_VA}}`,
        enableRounding: true,
      },
      {
        name: 'DEF_TAX_RATE',
        formula: `{{SOURCE_DEF_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'DEFTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'DEF_TAX_B4_FTC',
        formula: `{{TOT_DEF}} * {{DEF_TAX_RATE}}`,
        enableRounding: true,
      },
      {
        name: 'CUR_TAX',
        formula: `{{CUR_TAX_B4_CR}} + {{TOTAL_CR}} + {{CUR_FTC}} + {{FTC_LIMIT}}`,
        enableRounding: true,
      },
      {
        name: 'DEF_TAX',
        formula: `{{DEF_TAX_B4_FTC}} + {{DEF_FTC}} + {{DEF_PR_CR_UTL}}`,
        enableRounding: true,
      },
      {
        name: 'TAX_EXP_CY',
        formula: `{{CUR_TAX}} + {{DEF_TAX}}`,
        enableRounding: true,
      },
      {
        name: 'CY_EFF_TAX_RATE',
        formula: `{{TAX_EXP_CY}} / {{orNull 'IBT'}}`,
        disableCurrencyConversion: true,
      },
    ],
  },
  {
    NAME: 'US_STATE',
    CONDITION: ['US', 'SUBDIVISION'],
    SUMS: [
      {
        name: 'US_TXBL_INC_B4_STATE',
        formula: `{{report 'US_FEDERAL' 'ADJ_IBT'}} + {{report 'US_FEDERAL' 'PERM'}} + {{report 'US_FEDERAL' 'TEMP'}}`,
      },
      {
        name: 'STATE_PERM_MODS',
        formula: `{{sum 'BTT_PERM_DETAILS' 'PERM_DETAILS' 'PERM_MANUAL_DETAILS'}}`,
        import: {
          tag: 'STTAXPERM',
        },
      },
      {
        name: 'STATE_TEMP_MODS',
        formula: `{{sum 'BTT_TEMP_DETAILS' 'TEMP_DETAILS' 'TEMP_MANUAL_DETAILS'}}`,
        import: {
          tag: 'STTAXTEMP',
        },
      },
      {
        name: 'UTILIZE_PY_NOL_PRE',
        formula: [
          {
            when: [
              `{{OBLIGATION_STATE_PRE_POST_NOL}} = 'PRE'`,
            ],
            then: `{{NOL_TAX_ENTRIES}}`,
          },
          `null`,
        ],
        import: {
          tag: 'PRYRNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'STATE_TXBL_INC_B4_AP',
        formula: `{{US_TXBL_INC_B4_STATE}} + {{STATE_PERM_MODS}} + {{STATE_TEMP_MODS}} + {{UTILIZE_PY_NOL_PRE}}`,
      },
      {
        name: 'AP_RATE',
        formula: `{{OBLIGATION_STATE_APPT_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'APORRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'AP_TXBL_INC',
        formula: `{{STATE_TXBL_INC_B4_AP}} * {{AP_RATE}}`,
        enableRounding: true,
      },
      {
        name: 'UTILIZE_PY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_STATE_PRE_POST_NOL}} = 'POST'`,
              `{{OBLIGATION_NOL}}='UTILIZE'`,
            ],
            then: `{{NOL_TAX_ENTRIES}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'UTILNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'TXBL_INC_AFT_NOL',
        formula: `{{AP_TXBL_INC}} + {{UTILIZE_PY_NOL}}`,
      },
      {
        name: 'RECORD_CY_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{TXBL_INC_AFT_NOL}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'RECNOL',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'TAX_RATE',
        formula: `{{SOURCE_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'CURTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'CUR_TAX_B4_CR',
        formula: `({{TXBL_INC_AFT_NOL}} + {{RECORD_CY_NOL}}) * {{TAX_RATE}}`,
        enableRounding: true,
      },

      {
        name: 'CY_TEMP',
        formula: `-{{report 'US_FEDERAL' 'TEMP'}}`,
      },
      {
        name: 'STATE_MODS',
        formula: `-{{STATE_TEMP_MODS}}`,
      },
      {
        name: 'NO_NOL',
        formula: [
          {
            when: [
              `{{OBLIGATION_NOL}} = 'UTILIZE'`,
              `{{OBLIGATION_STATE_PRE_POST_NOL}} = 'PRE'`,
            ],
            then: `-{{UTILIZE_PY_NOL_PRE}}`,
          },
          {
            when: [
              `{{OBLIGATION_NOL}} = 'UTILIZE'`,
              `{{OBLIGATION_STATE_PRE_POST_NOL}} = 'POST'`,
            ],
            then: `-{{UTILIZE_PY_NOL}}`,
          },
          {
            when: [
              `{{OBLIGATION_NOL}} = 'RECORD'`,
            ],
            then: `{{STATE_TXBL_INC_B4_AP}}`,
          },
          `NULL`,
        ],
      },
      {
        name: 'RECORD_VA',
        formula: [
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='NOL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-{{NO_NOL}}`,
          },
          {
            when: [
              `{{OBLIGATION_RECORD_VA}}='ALL'`,
              `{{OBLIGATION_NOL}}='RECORD'`,
            ],
            then: `-({{CY_TEMP}} + {{NO_NOL}} + {{STATE_MODS}})`,
          },
          `NULL`,
        ],
      },
      {
        name: 'TOT_DEF',
        formula: `{{CY_TEMP}} + {{STATE_MODS}} + {{NO_NOL}} + {{RECORD_VA}}`,
        enableRounding: true,
      },
      {
        name: 'DEF_TAX_RATE',
        formula: `{{SOURCE_DEF_TAX_RATE}}`,
        disableCurrencyConversion: true,
        import: {
          tag: 'DEFTAXRATE',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'DEF_PR_CR_UTL',
        formula: `{{sum 'DEF_PR_CR_UTL_DETAILS'}}`,
        import: {
          tag: 'AFTAXDEFTE',
          type: IMPORT_TYPES.DETAIL_SYNC_TRANSFORM,
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
      },
      {
        name: 'DEF_TAX',
        formula: `{{TOT_DEF}} * {{DEF_TAX_RATE}} * {{AP_RATE}} + {{DEF_PR_CR_UTL}}`,
        enableRounding: true,
      },
      {
        name: 'STATE_MIN_TAX',
        formula: [
          {
            when: [
              `{{IS_SUBDIVISION}} = 'Y'`,
            ],
            then: `{{allowNull 'OBLIGATION_STATE_MINIMUM_TAX'}}`,
          },
          `NULL`,
        ],
        import: {
          tag: 'STMINTAX',
          type: IMPORT_TYPES.OVERRIDE,
        },
      },
      {
        name: 'MAX_CUR',
        formula: [
          {
            when: [
              `{{allowNull 'STATE_MIN_TAX'}} IS NULL`,
            ],
            then: `{{CUR_TAX_B4_CR}}`,
          },
          {
            when: [
              `{{CUR_TAX_B4_CR}} > {{STATE_MIN_TAX}}`,
            ],
            then: `{{CUR_TAX_B4_CR}}`,
          },
          `{{STATE_MIN_TAX}}`,
        ],
        enableRounding: true,
      },
      {
        name: 'TAX_CR_ADJ',
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS' 'GAAP_AB_CREDIT_DETAILS'}}`,
      },
      {
        name: 'TOTAL_CR',
        formula: `{{sum 'GAAP_MANUAL_CREDIT_DETAILS' 'GAAP_AB_CREDIT_DETAILS' 'PR_CR_UTL_DETAILS'}}`,
        import: {
          tag: 'TOTCR',
        },
      },
      {
        name: 'CUR_TAX',
        formula: `{{MAX_CUR}} + {{TOTAL_CR}}`,
      },
      {
        name: 'TAX_EXP_CY',
        formula: `{{CUR_TAX}} + {{DEF_TAX}}`,
        enableRounding: true,
      },
      {
        name: 'CY_EFF_TAX_RATE',
        formula: `{{TAX_EXP_CY}} / {{orNull 'US_TXBL_INC_B4_STATE'}}`,
        disableCurrencyConversion: true,
        enableRounding: true,
        roundingPrecision: 6,
      },
    ],
  },
];

enum EDITING_RULES_NAME {
  OVERRIDE = 'OVERRIDE',
  SYNC = 'SYNC',
  DETAIL = 'DETAIL',
  DETAIL_SYNC = 'DETAIL_SYNC',
  DETAIL_SYNC_TRANSFORM = 'DETAIL_SYNC_TRANSFORM',
}
type EditingRules = Record<string, {
  name: EDITING_RULES_NAME,
  defaultData?: Record<string, any>,
  columns?: typeof COLUMNS,
  valueColumn?: string,
}>;
const EDITING_RULES: EditingRules = {
  OVERRIDE: { name: EDITING_RULES_NAME.OVERRIDE },
  SYNC: { name: EDITING_RULES_NAME.SYNC },
  DETAIL: {
    name: EDITING_RULES_NAME.DETAIL,
    defaultData: {
      DETAIL_NAME: FAKE_DETAIL_NAME,
      ACCOUNT: DEFAULT_ACCOUNT,
      ADDED: YES_FLAG,
      DELETED: NO_FLAG,
    },
    columns: COLUMNS,
    valueColumn: COLUMNS.DETAILS.VALUE,
  },
  DETAIL_SYNC: {
    name: EDITING_RULES_NAME.DETAIL_SYNC,
    defaultData: {
      DETAIL_NAME: FAKE_DETAIL_NAME,
      ACCOUNT: DEFAULT_ACCOUNT,
      ADDED: YES_FLAG,
      DELETED: NO_FLAG,
    },
    columns: COLUMNS,
    valueColumn: COLUMNS.DETAILS.VALUE,
  },
  DETAIL_SYNC_TRANSFORM: {
    name: EDITING_RULES_NAME.DETAIL_SYNC_TRANSFORM,
    columns: COLUMNS,
    valueColumn: COLUMNS.DETAILS.VALUE,
    defaultData: {
      DETAIL_NAME: FAKE_DETAIL_NAME,
      ACCOUNT: DEFAULT_ACCOUNT,
      ADDED: YES_FLAG,
      DELETED: NO_FLAG,
    },
  },
};

enum REPORT_FIELDS_TYPES {
  SUMMARY = 'SUMMARY',
}

enum CALCULATE_TOTAL_TYPE {
  SUM_ALL = 'SUM_ALL',
  SUM_PER_COMPANY_CODE = 'SUM_PER_COMPANY_CODE',
}

export type ReportField = {
  unifiedName?: string,
  from: string | PartialRecord<Report['NAME'], string>,
  label: string,
  type?: REPORT_FIELDS_TYPES,
  editable?: true | PartialRecord<Report['NAME'], true | {
    type: EDITING_RULES_NAME,
    detailName?: string,
    reports?: PartialRecord<Report['NAME'], string[]>
    disable?: { name?: string }[];
    syncTransform?: { [detailName: string]: DETAIL_VALUE_TRANSFORM_NAME }
  }>
  hidden?: boolean,
  exportFormat?: CellExportFormats,
  calculateTotal?: CALCULATE_TOTAL_TYPE,
  cellFormat?: FormatTypes,
  displayNullAsZero?: boolean,
  disableCurrencyConversion?: boolean,
  hiddenConditions?: VISIBLE_FIELD_CONDITIONS[],
};
const REPORT_FIELDS: { TAX_EXPENSE: ReportField[] } = {
  TAX_EXPENSE: [
    {
      unifiedName: 'FUNCTIONAL_CURRENCY',
      from: 'FUNCTIONAL_CURRENCY',
      label: 'Functional Currency',
      hidden: false,
    },
    {
      unifiedName: 'IBT',
      from: {
        NON_US: 'GAAP_IBT',
        US_FEDERAL: 'IBT',
      },
      label: 'Income/(Loss) Before Tax',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL_SYNC.name,
          detailName: `GAAP_IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
          reports: {
            NON_US: [
              `GAAP_IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            ],
            US_FEDERAL: [
              `IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            ],
          },
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL_SYNC.name,
          detailName: `IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
          reports: {
            NON_US: [
              `GAAP_IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            ],
            US_FEDERAL: [
              `IBT${GENERIC_DETAIL_NAME_SUFFIX}`,
            ],
          },
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_PER_COMPANY_CODE,
    },
    {
      unifiedName: 'IBT',
      from: {
        US_STATE: 'US_TXBL_INC_B4_STATE',
      },
      label: 'Federal Taxable Income/(Loss) Before State',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_PER_COMPANY_CODE,
    },
    {
      from: {
        NON_US: 'GAAP_IBT_ADJ',
        US_FEDERAL: 'IBT_ADJ',
      },
      label: 'Pre-Tax Adjustment',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'IBT_ADJ_DETAILS',
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'IBT_ADJ_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'ADJ_GAAP_IBT',
        US_FEDERAL: 'ADJ_IBT',
      },
      label: 'Adjusted Income/(Loss) Before Tax',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'STATE_CUR_TAX',
      },
      label: 'State Income Tax',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'STATE_CUR_TAX_ADJ',
      },
      label: 'State Adjustment',
      editable: {
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'STATE_CUR_TAX_ADJ_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'G2S_PERM',
      },
      label: 'GAAP to STAT Non-Reversing Differences',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'G2S_BTT_PERM_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'G2S_TEMP',
      },
      label: 'GAAP to STAT Reversing Differences',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'G2S_MANUAL_TEMP_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'STAT_IBT',
      },
      label: 'Statutory Income/(Loss) Before Tax',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'B2T_PERM',
        US_FEDERAL: 'PERM',
      },
      label: 'Tax Permanent Differences',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'GAAP_BTT_PERM_DETAILS',
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'FEDERAL_BTT_PERM_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'B2T_TEMP',
        US_FEDERAL: 'TEMP',
      },
      label: 'Tax Temporary Differences',
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'GAAP_BTT_TEMP_DETAILS',
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'FEDERAL_BTT_TEMP_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_STATE: 'STATE_PERM_MODS',
      },
      label: 'State Tax Permanent Modifications',
      editable: {
        US_STATE: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'PERM_MANUAL_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_STATE: 'STATE_TEMP_MODS',
      },
      label: 'State Tax Temporary Modifications',
      editable: {
        US_STATE: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'BTT_TEMP_DETAILS',
        },
      },
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    // Tax expense before credits
    {
      from: {
        US_STATE: 'UTILIZE_PY_NOL_PRE',
      },
      editable: {
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
          // Conditions are used to limit when the field is editable. Usage left here for reference.
          // conditions: [
          //   EDITABLE_FIELD_CONDITIONS.OBLIGATION_STATE_PRE_NOL,
          // ],
        },
      },
      label: 'Utilize Prior Year NOL\'s pre Apportionment',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      displayNullAsZero: true,
    },
    {
      from: {
        NON_US: 'TXBL_INC',
        US_FEDERAL: 'TXBL_INC',
        US_STATE: 'STATE_TXBL_INC_B4_AP',
      },
      label: 'Taxable Income/(Loss)',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_STATE: 'AP_RATE',
      },
      label: 'Apportionment Rate',
      editable: {
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      cellFormat: FormatTypes.PERCENT_4,
      exportFormat: CellExportFormats.NUMBER,
    },
    {
      from: {
        US_STATE: 'AP_TXBL_INC',
      },
      label: 'Apportioned Taxable Income/(Loss)',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'UTILIZE_PY_NOL',
        US_FEDERAL: 'UTILIZE_PY_NOL',
        US_STATE: 'UTILIZE_PY_NOL',
      },
      editable: {
        NON_US: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_FEDERAL: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      label: 'Utilize Prior Year NOL\'s',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      displayNullAsZero: true,
    },
    {
      from: {
        NON_US: 'TXBL_INC_AFT_NOL',
        US_FEDERAL: 'TXBL_INC_AFT_NOL',
        US_STATE: 'TXBL_INC_AFT_NOL',
      },
      label: 'Taxable Income After NOL Utilization',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'RECORD_CY_NOL',
        US_FEDERAL: 'RECORD_CY_NOL',
        US_STATE: 'RECORD_CY_NOL',
      },
      editable: {
        NON_US: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_FEDERAL: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      label: 'Record Current Year NOL',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      displayNullAsZero: true,
    },
    {
      from: 'TAX_RATE',
      label: 'Current Tax Rate',
      editable: {
        NON_US: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_FEDERAL: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      disableCurrencyConversion: true,
      cellFormat: FormatTypes.PERCENTAGE,
      exportFormat: CellExportFormats.PERCENT_6,
    },
    {
      from: {
        NON_US: 'GAAP_CUR_TAX_B4_CR',
        US_FEDERAL: 'CUR_TAX_B4_CR',
        US_STATE: 'CUR_TAX_B4_CR',
      },
      label: 'Current Tax Expense Before Credits',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    // doesn't seem to be visible in provision
    // {
    //   from: 'GAAP_TAX_CR_ADJ',
    //   label: 'GAAP Manual Tax Credit Adjustment',
    // },

    // credit
    {
      from: 'TOTAL_CR',
      label: 'Credits and Other After Tax Adjustments',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'GAAP_MANUAL_CREDIT_DETAILS',
          disable: [{ name: 'PR_CR_UTL_DETAILS' }],
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'GAAP_MANUAL_CREDIT_DETAILS',
          disable: [{ name: 'PR_CR_UTL_DETAILS' }],
        },
        US_STATE: {
          type: EDITING_RULES.DETAIL.name,
          detailName: 'GAAP_MANUAL_CREDIT_DETAILS',
          disable: [{ name: 'PR_CR_UTL_DETAILS' }],
        },
      },
    },
    {
      from: {
        US_FEDERAL: 'CUR_FTC',
      },
      label: 'Foreign Current Tax Expense',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'DEF_FTC',
      },
      label: 'Foreign Deferred Tax Expense',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'FTC_LIMIT',
      },
      label: 'Credit Limitation',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'BEN_FTC_CR',
      },
      label: 'Total Benefited Credits',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_STATE: 'STATE_MIN_TAX',
      },
      label: 'Minimum Tax',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      editable: {
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      displayNullAsZero: true,
    },
    {
      from: {
        NON_US: 'GAAP_TOT_DEF',
      },
      label: 'GAAP Deferreds',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'TOT_DEF',
      },
      label: 'STAT Deferreds',
      hiddenConditions: [
        VISIBLE_FIELD_CONDITIONS.COMPANY_CODE_STAT_CALC_DISABLED,
      ],
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_FEDERAL: 'TOT_DEF',
        US_STATE: 'TOT_DEF',
      },
      label: 'Deferreds',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'DEF_TAX_RATE',
        US_FEDERAL: 'DEF_TAX_RATE',
        US_STATE: 'DEF_TAX_RATE',
      },
      label: 'Deferred Tax Rate',
      editable: {
        NON_US: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_FEDERAL: {
          type: EDITING_RULES.OVERRIDE.name,
        },
        US_STATE: {
          type: EDITING_RULES.OVERRIDE.name,
        },
      },
      cellFormat: FormatTypes.PERCENTAGE,
      exportFormat: CellExportFormats.PERCENT_6,
    },
    {
      from: {
        NON_US: 'GAAP_GAAP_DEF_TAX',
        US_FEDERAL: 'DEF_TAX',
      },
      label: 'GAAP Deferred Tax Expense',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'GAAP_DEF_TAX',
      },
      hiddenConditions: [
        VISIBLE_FIELD_CONDITIONS.COMPANY_CODE_STAT_CALC_DISABLED,
      ],
      label: 'STAT Deferred Tax Expense',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      label: 'After Tax Deferred Tax Expense',
      from: {
        NON_US: 'GAAP_DEF_PR_CR_UTL',
        US_FEDERAL: 'DEF_PR_CR_UTL',
        US_STATE: 'DEF_PR_CR_UTL',
      },
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
      editable: {
        NON_US: {
          type: EDITING_RULES.DETAIL_SYNC_TRANSFORM.name,
          detailName: 'DEF_PR_CR_UTL_DETAILS',
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
        US_FEDERAL: {
          type: EDITING_RULES.DETAIL_SYNC_TRANSFORM.name,
          detailName: 'DEF_PR_CR_UTL_DETAILS',
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
        US_STATE: {
          type: EDITING_RULES.DETAIL_SYNC_TRANSFORM.name,
          detailName: 'DEF_PR_CR_UTL_DETAILS',
          syncTransform: {
            DEF_PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NOOP,
            PR_CR_UTL_DETAILS: DETAIL_VALUE_TRANSFORM_NAME.NEGATION,
          },
        },
      },
    },
    {
      from: {
        US_STATE: 'DEF_TAX',
      },
      label: 'Deferred Tax Expense',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'GAAP_CUR_TAX',
        US_FEDERAL: 'CUR_TAX',
      },
      label: 'GAAP Current Tax Expense - Functional Currency',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'GAAP_GAAP_DEF_TAX',
        US_FEDERAL: 'DEF_TAX',
      },
      label: 'GAAP Deferred Tax Expense - Functional currency',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      unifiedName: 'TOTAL_TAX_EXPENSE',
      from: {
        NON_US: 'GAAP_TAX_EXP_CY',
        US_FEDERAL: 'TAX_EXP_CY',
      },
      label: 'GAAP Tax Expense on CY Activity - Functional Currency',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      unifiedName: 'ETR',
      from: {
        NON_US: 'GAAP_CY_EFF_TAX_RATE',
        US_FEDERAL: 'CY_EFF_TAX_RATE',
      },
      label: 'GAAP CY Effective Tax Rate',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      cellFormat: FormatTypes.PERCENTAGE,
      exportFormat: CellExportFormats.PERCENT_6,
    },
    {
      from: {
        NON_US: 'GAAP_DEF_TAX',
      },
      label: 'STAT Deferred Tax Expense - Functional Currency',
      hiddenConditions: [
        VISIBLE_FIELD_CONDITIONS.COMPANY_CODE_STAT_CALC_DISABLED,
      ],
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        NON_US: 'TAX_EXP_CY',
      },
      label: 'STAT Tax Expense on CY Activity - Functional Currency',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      unifiedName: 'ETR',
      from: {
        NON_US: 'CY_EFF_TAX_RATE',
      },
      label: 'STAT CY Effective Tax Rate',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      cellFormat: FormatTypes.PERCENTAGE,
      exportFormat: CellExportFormats.PERCENT_6,
    },
    {
      from: {
        US_STATE: 'CUR_TAX',
      },
      label: 'Current Tax Expense on CY Activity',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      from: {
        US_STATE: 'DEF_TAX',
      },
      label: 'Deferred Tax Expense on CY Activity',
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      unifiedName: 'TOTAL_TAX_EXPENSE',
      from: {
        US_STATE: 'TAX_EXP_CY',
      },
      label: 'Tax Expense on CY Activity - Functional Currency',
      type: REPORT_FIELDS_TYPES.SUMMARY,
      exportFormat: CellExportFormats.NUMBER,
      calculateTotal: CALCULATE_TOTAL_TYPE.SUM_ALL,
    },
    {
      unifiedName: 'ETR',
      from: {
        US_STATE: 'CY_EFF_TAX_RATE',
      },
      label: 'CY Effective Tax Rate',
      cellFormat: FormatTypes.PERCENTAGE,
      exportFormat: CellExportFormats.PERCENT_6,
    },
  ],
};

const TOUCHED_FLAG = 'TOUCHED_FLAG';
const TOUCHED_FLAG_SUFFIX = '_TOUCHED_FLAG';

const MAX_NUMBER_OF_MEMBERS_PER_SCENARIO = 1000;

export {
  COLUMNS,
  OUTPUT_COLUMN_NAMES,
  COLUMNS_TO_COPY,
  SCENARIO_DATA_TABLES,
  DETAILS_DEFINITIONS,
  REPORTS,
  EDITING_RULES_NAME,
  REPORT_FIELDS,
  OVERRIDE_COLUMN_SUFFIX,
  EDITING_RULES,
  FAKE_DETAIL_NAME,
  GENERIC_DETAIL_NAME_SUFFIX,
  IMPORTED_DETAIL_ID_PREFIX,
  IMPORT_TYPES,
  IMPORT_TYPES_SETTINGS,
  SOURCE_TABLES,
  IMPORT_VALIDATION_RULES,
  TOUCHED_FLAG,
  TOUCHED_FLAG_SUFFIX,
  DEFAULT_ACCOUNT,
  MAX_NUMBER_OF_MEMBERS_PER_SCENARIO,
  DETAIL_VALUE_TRANSFORM,
  BatchDetailsOperation,
};
